import { useCallback, useMemo, useSyncExternalStore } from 'react';

export interface NetworkStatusSnapshot {
  online: boolean;
  /** Timestamp when the connection last changed (ISO string). */
  changedAt: string;
}

const isNavigatorOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine);

let lastSnapshot: NetworkStatusSnapshot = {
  online: isNavigatorOnline(),
  changedAt: new Date().toISOString()
};

const emitSnapshot = () => lastSnapshot;

const updateSnapshot = () => {
  const online = isNavigatorOnline();
  if (online !== lastSnapshot.online) {
    lastSnapshot = { online, changedAt: new Date().toISOString() };
  }
};

const listeners = new Set<() => void>();
let detachListeners: (() => void) | null = null;

const notifySubscribers = () => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.warn('[network] subscriber failed during notify', error);
    }
  });
};

const attachListeners = () => {
  if (typeof window === 'undefined' || detachListeners) {
    return;
  }

  const handleNetworkChange = () => {
    updateSnapshot();
    notifySubscribers();
  };

  const handleVisibilityChange = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    handleNetworkChange();
  };

  window.addEventListener('online', handleNetworkChange);
  window.addEventListener('offline', handleNetworkChange);
  window.addEventListener('pageshow', handleNetworkChange);
  window.addEventListener('focus', handleNetworkChange);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  detachListeners = () => {
    window.removeEventListener('online', handleNetworkChange);
    window.removeEventListener('offline', handleNetworkChange);
    window.removeEventListener('pageshow', handleNetworkChange);
    window.removeEventListener('focus', handleNetworkChange);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    detachListeners = null;
  };
};

const subscribe = (listener: () => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  listeners.add(listener);
  attachListeners();

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && detachListeners) {
      detachListeners();
    }
  };
};

const getServerSnapshot = () => lastSnapshot;

export function useNetworkStatus(): NetworkStatusSnapshot {
  const snapshot = useSyncExternalStore(subscribe, emitSnapshot, getServerSnapshot);
  return useMemo(() => snapshot, [snapshot.online, snapshot.changedAt]);
}

export function useIsOnline() {
  const { online } = useNetworkStatus();
  return useCallback(() => online, [online]);
}

export function refreshNetworkStatusSnapshot() {
  updateSnapshot();
  notifySubscribers();
}
